/**
 * Hand-off for the cross-mode approval banners' "Respond" action.
 *
 * Respond navigates between the published page and the editor, and that navigation tears down
 * the right sidebar (see `useCloseApprovalsPaneOnNavigation`). The intent records which content
 * and mode should open the approvals pane, so the destination's `ApprovalsProvider` can open it
 * once that mode's approval data has loaded.
 *
 * Both view → edit and edit → view are SPA route pushes, so module state survives them and no
 * sessionStorage mirror is needed (unlike `liveDocApprovalRedirect.ts`, which survives a full
 * page load). A short TTL keeps an abandoned navigation from opening the pane later.
 *
 * Callers MUST only set the intent behind `confluence_approvals_banner_updates_exp`.
 */

export type CrossModeApprovalRespondIntent = {
	contentId: string;
	contentStatus: 'CURRENT' | 'DRAFT';
	createdAt: number;
};

const INTENT_TTL_MS = 2 * 60 * 1000;

let inMemoryIntent: CrossModeApprovalRespondIntent | null = null;

export const setCrossModeApprovalRespondIntent = ({
	contentId,
	contentStatus,
}: Omit<CrossModeApprovalRespondIntent, 'createdAt'>): void => {
	inMemoryIntent = { contentId, contentStatus, createdAt: Date.now() };
};

export const clearCrossModeApprovalRespondIntent = (): void => {
	inMemoryIntent = null;
};

export const getCrossModeApprovalRespondIntent = (): CrossModeApprovalRespondIntent | null => {
	if (inMemoryIntent && Date.now() - inMemoryIntent.createdAt > INTENT_TTL_MS) {
		inMemoryIntent = null;
	}
	return inMemoryIntent;
};
